import React, { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { MonthlyBarChart } from '../components/Charts';
import {
  FaFireAlt, FaArrowUp, FaArrowDown, FaBalanceScale,
  FaCalendarAlt, FaTrophy, FaExclamationTriangle
} from 'react-icons/fa';

const categoryEmoji = {
  Food: '🍔', Salary: '💼', Utilities: '⚡', Electronics: '💻',
  Health: '💪', Transport: '🚗', Housing: '🏠', Entertainment: '🎬',
  Healthcare: '🏥', Shopping: '🛍️', Investment: '📈', Other: '📁',
};

const Insights = () => {
  const { transactions, formatCurrency } = useApp();

  const insights = useMemo(() => {
    if (transactions.length === 0) return null;

    // Monthly breakdown
    const monthlyMap = {};
    transactions.forEach(tx => {
      const d = new Date(tx.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[key]) monthlyMap[key] = { income: 0, expense: 0, txCount: 0 };
      if (tx.type === 'income') monthlyMap[key].income += tx.amount;
      else monthlyMap[key].expense += tx.amount;
      monthlyMap[key].txCount += 1;
    });

    const months = Object.entries(monthlyMap).sort(([a], [b]) => a.localeCompare(b));
    const latestMonth = months[months.length - 1];
    const prevMonth = months[months.length - 2];

    // Category spending
    const categoryMap = {};
    transactions.filter(t => t.type === 'expense').forEach(t => {
      categoryMap[t.category] = (categoryMap[t.category] || 0) + t.amount;
    });
    const sortedCategories = Object.entries(categoryMap).sort(([, a], [, b]) => b - a);
    const topCategory = sortedCategories[0];
    const leastCategory = sortedCategories[sortedCategories.length - 1];

    // Savings rate
    const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0);
    const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0);
    const savingsRate = totalIncome > 0 ? ((totalIncome - totalExpense) / totalIncome * 100).toFixed(1) : 0;

    // MoM comparison
    let momChange = null;
    if (latestMonth && prevMonth) {
      const latestExpense = latestMonth[1].expense;
      const prevExpense = prevMonth[1].expense;
      momChange = prevExpense > 0 ? (((latestExpense - prevExpense) / prevExpense) * 100).toFixed(1) : null;
    }

    // Best month (highest savings = income - expense)
    const bestMonth = months.reduce((best, [month, data]) => {
      const savings = data.income - data.expense;
      if (!best || savings > best.savings) return { month, savings, ...data };
      return best;
    }, null);

    // Avg monthly spend
    const avgMonthlyExpense = months.length > 0
      ? parseFloat((months.reduce((sum, [, d]) => sum + d.expense, 0) / months.length).toFixed(2))
      : 0;

    // Highest single transaction
    const expenseTransactions = transactions.filter(t => t.type === 'expense');
    const highestTx = expenseTransactions.reduce((max, t) => t.amount > (max?.amount || 0) ? t : max, null);

    return {
      totalIncome, totalExpense, savingsRate, topCategory, leastCategory,
      momChange, bestMonth, avgMonthlyExpense, highestTx,
      latestMonth, prevMonth, txCount: transactions.length, monthCount: months.length,
    };
  }, [transactions]);

  if (!insights) {
    return (
      <div className="fade-in" style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
        <span style={{ fontSize: '3rem' }}>📊</span>
        <h2 style={{ marginTop: '1rem', color: 'var(--text-primary)' }}>No Data Yet</h2>
        <p>Add some transactions to see insights.</p>
      </div>
    );
  }

  const formatMonth = (key) => {
    if (!key) return 'N/A';
    const [y, m] = key.split('-');
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const InsightCard = ({ icon: Icon, iconColor, iconBg, title, value, sub, accent }) => (
    <div className="card fade-in" style={{
      transition: 'transform 0.2s ease',
      cursor: 'default',
    }}
      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '14px',
          background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={20} color={iconColor} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }}>{title}</p>
          <p style={{ fontWeight: 800, fontSize: '1.3rem', color: accent || 'var(--text-primary)', letterSpacing: '-0.02em' }}>{value}</p>
          {sub && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>{sub}</p>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Header */}
      <div>
        <h1 className="page-title">Insights</h1>
        <p className="page-subtitle">Smart analysis of your financial activity</p>
      </div>

      {/* Key Metrics */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>Key Metrics</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
          <InsightCard
            icon={FaTrophy} iconColor="#f59e0b" iconBg="rgba(245,158,11,0.15)"
            title="Savings Rate"
            value={`${insights.savingsRate}%`}
            sub={insights.savingsRate > 20 ? '✅ Healthy savings rate' : '⚠️ Consider reducing expenses'}
            accent={insights.savingsRate > 20 ? 'var(--success)' : 'var(--warning)'}
          />
          <InsightCard
            icon={FaCalendarAlt} iconColor="#6366f1" iconBg="rgba(99,102,241,0.15)"
            title="Avg Monthly Spend"
            value={formatCurrency(insights.avgMonthlyExpense)}
            sub={`Across ${insights.monthCount} months`}
          />
          <InsightCard
            icon={FaFireAlt} iconColor="#ef4444" iconBg="rgba(239,68,68,0.15)"
            title="Top Spending Category"
            value={insights.topCategory ? `${categoryEmoji[insights.topCategory[0]] || '📁'} ${insights.topCategory[0]}` : 'N/A'}
            sub={insights.topCategory ? formatCurrency(insights.topCategory[1]) + ' total' : ''}
            accent="var(--danger)"
          />
          <InsightCard
            icon={FaBalanceScale} iconColor="#10b981" iconBg="rgba(16,185,129,0.15)"
            title="Net Balance"
            value={formatCurrency(insights.totalIncome - insights.totalExpense)}
            sub={`Income: ${formatCurrency(insights.totalIncome)} | Expenses: ${formatCurrency(insights.totalExpense)}`}
            accent={insights.totalIncome >= insights.totalExpense ? 'var(--success)' : 'var(--danger)'}
          />
        </div>
      </div>

      {/* Monthly Comparison Chart */}
      <div className="card">
        <div style={{ marginBottom: '1.25rem' }}>
          <h2 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>Monthly Income vs Expenses</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Last 6 months comparison</p>
        </div>
        <MonthlyBarChart transactions={transactions} />
      </div>

      {/* Month-over-Month + Best Month */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
        {/* MoM Change */}
        {insights.momChange !== null && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              {parseFloat(insights.momChange) > 0 ? (
                <FaArrowUp color="var(--danger)" />
              ) : (
                <FaArrowDown color="var(--success)" />
              )}
              <h3 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>Month-over-Month</h3>
            </div>
            <div style={{
              padding: '1.25rem',
              borderRadius: '12px',
              background: parseFloat(insights.momChange) > 0 ? 'var(--danger-light)' : 'var(--success-light)',
              textAlign: 'center',
            }}>
              <p style={{
                fontSize: '2.5rem', fontWeight: 800,
                color: parseFloat(insights.momChange) > 0 ? 'var(--danger)' : 'var(--success)',
                letterSpacing: '-0.02em',
              }}>
                {parseFloat(insights.momChange) > 0 ? '+' : ''}{insights.momChange}%
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                {parseFloat(insights.momChange) > 0 ? 'spending increased' : 'spending decreased'} vs last month
              </p>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', gap: '0.75rem' }}>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{formatMonth(insights.prevMonth?.[0])}</p>
                <p style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{formatCurrency(insights.prevMonth?.[1].expense || 0)}</p>
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>{formatMonth(insights.latestMonth?.[0])}</p>
                <p style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{formatCurrency(insights.latestMonth?.[1].expense || 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Best Month */}
        {insights.bestMonth && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <FaTrophy color="var(--warning)" />
              <h3 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>Best Month</h3>
            </div>
            <div style={{ padding: '1.25rem', borderRadius: '12px', background: 'var(--success-light)', textAlign: 'center', marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--success)', fontWeight: 600, marginBottom: '0.3rem' }}>
                {formatMonth(insights.bestMonth.month)}
              </p>
              <p style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--success)', letterSpacing: '-0.02em' }}>
                +{formatCurrency(insights.bestMonth.savings)}
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>net savings</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ textAlign: 'center', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Income</p>
                <p style={{ fontWeight: 700, color: 'var(--success)', fontSize: '0.9rem' }}>{formatCurrency(insights.bestMonth.income)}</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border)' }}>
                <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Expenses</p>
                <p style={{ fontWeight: 700, color: 'var(--danger)', fontSize: '0.9rem' }}>{formatCurrency(insights.bestMonth.expense)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Highest Transaction */}
        {insights.highestTx && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <FaExclamationTriangle color="var(--warning)" />
              <h3 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>Largest Expense</h3>
            </div>
            <div style={{ padding: '1.25rem', borderRadius: '12px', background: 'var(--danger-light)', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '2rem' }}>{categoryEmoji[insights.highestTx.category] || '📁'}</span>
                <div>
                  <p style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.2rem' }}>{insights.highestTx.title}</p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {insights.highestTx.category} · {new Date(insights.highestTx.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <p style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--danger)', marginTop: '1rem', letterSpacing: '-0.02em' }}>
                -{formatCurrency(insights.highestTx.amount)}
              </p>
            </div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              This represents {((insights.highestTx.amount / insights.totalExpense) * 100).toFixed(1)}% of your total expenses
            </p>
          </div>
        )}

        {/* Least Category */}
        {insights.leastCategory && insights.leastCategory !== insights.topCategory && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <FaArrowDown color="var(--success)" />
              <h3 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)' }}>Lowest Spending</h3>
            </div>
            <div style={{ padding: '1.25rem', borderRadius: '12px', background: 'var(--success-light)', textAlign: 'center' }}>
              <span style={{ fontSize: '2.5rem' }}>{categoryEmoji[insights.leastCategory[0]] || '📁'}</span>
              <p style={{ fontWeight: 700, fontSize: '1.2rem', color: 'var(--text-primary)', marginTop: '0.5rem' }}>{insights.leastCategory[0]}</p>
              <p style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--success)', marginTop: '0.25rem' }}>
                {formatCurrency(insights.leastCategory[1])}
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.3rem' }}>total spending</p>
            </div>
          </div>
        )}
      </div>

      {/* Observations */}
      <div className="card">
        <h2 style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1.25rem' }}>💡 Smart Observations</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {[
            insights.savingsRate > 30 && { icon: '🏆', text: `Outstanding! You're saving ${insights.savingsRate}% of your income.`, color: 'var(--success)' },
            insights.savingsRate > 0 && insights.savingsRate <= 30 && { icon: '👍', text: `You're saving ${insights.savingsRate}% of income. Financial advisors recommend 20%+.`, color: 'var(--warning)' },
            insights.savingsRate <= 0 && { icon: '⚠️', text: `Your expenses exceed income. Review your spending to improve your financial health.`, color: 'var(--danger)' },
            insights.topCategory && { icon: '🔥', text: `${insights.topCategory[0]} is your biggest expense category at ${formatCurrency(insights.topCategory[1])}.`, color: 'var(--text-primary)' },
            insights.momChange !== null && parseFloat(insights.momChange) > 15 && { icon: '📈', text: `Expenses increased significantly by ${insights.momChange}% month-over-month. Review this month's spending.`, color: 'var(--danger)' },
            insights.momChange !== null && parseFloat(insights.momChange) < -10 && { icon: '✅', text: `Great job! Expenses dropped by ${Math.abs(insights.momChange)}% compared to last month.`, color: 'var(--success)' },
            { icon: '📊', text: `You've recorded ${insights.txCount} transactions across ${insights.monthCount} months.`, color: 'var(--text-secondary)' },
          ].filter(Boolean).map((obs, i) => (
            <div key={i} style={{
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
              padding: '0.875rem 1rem', borderRadius: '12px',
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{obs.icon}</span>
              <p style={{ fontSize: '0.875rem', color: obs.color, lineHeight: 1.6 }}>{obs.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Insights;
